import { ClockCircleOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Col, Form, Row, Space, Tag, Typography, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { LanguageList } from '@/components/language';
import { LocationList, LocationListFooter } from '@/components/location';
import { LanguageListFooter, ResourceDocumentList, ResourceForm } from '@/components/resource';
import { DeleteButton, EditSaveButton } from '@/components/shared';
import { useDelayedLoading } from '@/hooks';
import { queryClient, queryKeys } from '@/lib/react-query';
import { Resource } from '@/models';
import { ROUTE_CONSTANTS } from '@/routes';
import { DATETIME_WITHOUT_DAY_FORMAT, formatResourceName, showGender, showMessage } from '@/utils';
import { useLanguages } from '@/web-api/languages';
import { useLocations } from '@/web-api/locations';
import {
  useAddLanguageToResource,
  useDeleteResource,
  useDeleteResourceDocument,
  useReactivateResource,
  useRemoveLanguageFromResource,
  useResource,
  useResourceDocuments,
  useSetResourceLocations,
  useUpdateResource,
} from '@/web-api/resources';
import { useUploadResourceDocument } from '@/web-api/resources/useUploadResourceDocument';

import { ResourceStats } from '.';
const { Text } = Typography;

export const ResourceDetailPage = () => {
  const [isEditingMode, setEditingMode] = useState(false);
  const { t } = useTranslation();
  const { resourceId } = useParams();
  if (resourceId === undefined) {
    throw new Error('Expected resourceId to be defined');
  }
  const { data, isLoading } = useResource(resourceId);
  const { data: documents, isLoading: isDocumentsLoading } = useResourceDocuments(resourceId);
  const showDelayedLoading = useDelayedLoading(isLoading);
  const languagesQuery = useLanguages();
  const locationsQuery = useLocations();
  const deleteResourceMutation = useDeleteResource();
  const reactivateResourceMutation = useReactivateResource();
  const updateResourceMutation = useUpdateResource();
  const addLanguageToResource = useAddLanguageToResource();
  const removeLanguageFromResource = useRemoveLanguageFromResource();
  const setResourceLocations = useSetResourceLocations();
  const uploadResourceDocument = useUploadResourceDocument(resourceId);
  const deleteResourceDocument = useDeleteResourceDocument();

  const navigate = useNavigate();

  const [form] = Form.useForm<Resource>();
  const [file, setFile] = useState<RcFile | null>(null);

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data);
    }
  }, [data, form]);

  const onSave = () => form.submit();
  const onCancel = () => {
    form.resetFields();
    setEditingMode(!isEditingMode);
  };

  function onReactivate() {
    if (resourceId === undefined) {
      throw new Error('Expected resourceId to be defined');
    }
    reactivateResourceMutation.mutate(resourceId, {
      onSuccess: () => {
        showMessage('success', t('common:reactivated'));
      },
    });
  }

  function onDelete() {
    if (resourceId === undefined) {
      throw new Error('Expected resourceId to be defined');
    }
    deleteResourceMutation.mutate(resourceId, {
      onSuccess: () => {
        showMessage('success', t('forms:deleted'));
        void queryClient.invalidateQueries({ queryKey: queryKeys.resources.all });
        void navigate(`/admin/${ROUTE_CONSTANTS.RESOURCES_PATH}`, { replace: true });
      },
    });
  }

  function onDeleteDocument(id: string) {
    deleteResourceDocument.mutate(id, {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.resourceDocuments.lists(resourceId ?? ''),
        });
        showMessage('success', t('forms:deleted'));
      },
    });
  }

  const onFormFinish = (resource: Resource) => {
    updateResourceMutation.mutate(resource, {
      onSuccess: () => {
        showMessage('success', t('forms:saved'));
        void queryClient.invalidateQueries({ queryKey: queryKeys.resources.detail(resource.id) });
        setEditingMode(false);
      },
    });
  };

  const onSelectSubmit = (serviceIds: string[]) => {
    addLanguageToResource.mutate(
      { resourceId, languageIds: serviceIds },
      {
        onSuccess: () => {
          showMessage('success', t('forms:saved'));
          void queryClient.invalidateQueries({ queryKey: queryKeys.resources.detail(resourceId) });
          setEditingMode(false);
        },
      }
    );
  };

  const onRemoveService = (serviceId: string) => {
    removeLanguageFromResource.mutate(
      { resourceId, languageId: serviceId },
      {
        onSuccess: () => {
          showMessage('success', t('forms:saved'));
          void queryClient.invalidateQueries({ queryKey: queryKeys.resources.detail(resourceId) });
          setEditingMode(false);
        },
      }
    );
  };

  const onAddLocations = (locationIds: string[]) => {
    const currentLocationIds = data?.locations?.map((l) => l.id) ?? [];
    const newLocationIds = [...currentLocationIds, ...locationIds];
    setResourceLocations.mutate(
      { resourceId, locationIds: newLocationIds },
      {
        onSuccess: () => {
          showMessage('success', t('forms:saved'));
          void queryClient.invalidateQueries({
            queryKey: queryKeys.resources.detail(resourceId),
          });
        },
      }
    );
  };

  const onRemoveLocation = (locationId: string) => {
    const currentLocationIds = data?.locations?.map((l) => l.id) ?? [];
    const newLocationIds = currentLocationIds.filter((id) => id !== locationId);
    setResourceLocations.mutate(
      { resourceId, locationIds: newLocationIds },
      {
        onSuccess: () => {
          showMessage('success', t('forms:saved'));
          void queryClient.invalidateQueries({
            queryKey: queryKeys.resources.detail(resourceId),
          });
        },
      }
    );
  };

  const uploadProps: UploadProps = {
    name: 'file',
    showUploadList: false,
    multiple: false,
    beforeUpload: (file) => {
      setFile(file);
      return false;
    },
  };

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} xl={24}>
        <Card
          title={
            <Space orientation='horizontal'>
              {data?.hasMissingInformation ? <Badge status='error' /> : undefined}
              {formatResourceName(data?.firstname ?? '', data?.lastname ?? '', 'non-formal')}
              {showGender(data?.gender)}
              {data?.isArchived ? (
                <Tag color='red'>{t('common:archived')}</Tag>
              ) : (
                <Tag color='success'>{t('common:active')}</Tag>
              )}
              {data?.lastActivity ? (
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  <ClockCircleOutlined />
                  &nbsp;
                  {t('common:lastActivity')}:&nbsp;
                  {dayjs().diff(dayjs(data?.lastActivity), 'day') > 0
                    ? dayjs(data?.lastActivity).format(DATETIME_WITHOUT_DAY_FORMAT)
                    : dayjs(data?.lastActivity).fromNow()}
                </Text>
              ) : undefined}
            </Space>
          }
          extra={
            <Space>
              <DeleteButton
                onDelete={onDelete}
                onReactivate={onReactivate}
                isArchived={data?.isArchived}
              />
              <EditSaveButton
                form={form}
                onEdit={() => setEditingMode(true)}
                isEditing={isEditingMode}
                onSave={onSave}
                onCancel={onCancel}
                isDisabled={false}
              />
            </Space>
          }
          loading={showDelayedLoading}
        >
          <Text copyable={{ tooltips: false, text: data?.number.toString() }}>
            {t('common:resourceNumber')}: {data?.number}
          </Text>
          <ResourceForm
            form={form}
            resource={data}
            onFinish={onFormFinish}
            isEditingMode={isEditingMode}
            mode='update'
          />
        </Card>
      </Col>
      <Col xs={24} xl={24}>
        <Card
          title={t('common:documents')}
          extra={
            <Space orientation='horizontal'>
              <Button
                type='default'
                hidden={!file}
                disabled={uploadResourceDocument.isPending}
                onClick={() => setFile(null)}
                icon={<CloseOutlined />}
              >
                {t('forms:discard')}
              </Button>
              <Button
                hidden={!file}
                type='primary'
                loading={uploadResourceDocument.isPending}
                onClick={() =>
                  file &&
                  uploadResourceDocument.mutate(
                    { file: file, fileName: file.name },
                    {
                      onSuccess: () => {
                        showMessage('success', t('forms:uploaded'));
                        void queryClient.invalidateQueries({
                          queryKey: queryKeys.resourceDocuments.lists(resourceId ?? ''),
                        });
                        setFile(null);
                      },
                    }
                  )
                }
              >
                {t('forms:upload')}
              </Button>
            </Space>
          }
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} xl={12}>
              <ResourceDocumentList
                documents={documents ?? []}
                isLoading={isDocumentsLoading}
                onDelete={onDeleteDocument}
              />
            </Col>
            <Col xs={24} xl={12}>
              <Dragger {...uploadProps}>
                <p className='ant-upload-drag-icon'>
                  <UploadOutlined />
                </p>
                {file ? (
                  <p className='ant-upload-text'>{file.name}</p>
                ) : (
                  <>
                    <p className='ant-upload-text'>{t('forms:uploadFileText')}</p>
                    <p className='ant-upload-hint'>{t('forms:uploadFileHint')}</p>
                  </>
                )}
              </Dragger>
            </Col>
          </Row>
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <ResourceStats resourceId={resourceId} />
      </Col>
      <Col xs={24} xl={12}>
        <Card title={t('common:languages')}>
          <LanguageList
            languages={data?.languages}
            onRemove={onRemoveService}
            isLoading={isLoading}
            footer={
              <LanguageListFooter
                languages={
                  data?.languages?.length
                    ? languagesQuery?.data?.filter(
                        (language) =>
                          !data?.languages
                            ?.map((resourceLanguages) => resourceLanguages.id)
                            .includes(language.id)
                      )
                    : languagesQuery.data
                }
                onSubmit={onSelectSubmit}
              />
            }
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title={t('common:locations')}>
          <LocationList
            locations={data?.locations}
            onRemove={onRemoveLocation}
            isLoading={isLoading}
            footer={
              <LocationListFooter
                locations={
                  data?.locations?.length
                    ? locationsQuery?.data?.filter(
                        (location) => !data?.locations?.map((rl) => rl.id).includes(location.id)
                      )
                    : locationsQuery.data
                }
                onSubmit={onAddLocations}
              />
            }
          />
        </Card>
      </Col>
    </Row>
  );
};
